import React, { useState } from 'react';
import useEventListener from '@/hooks/useEvenListener';
import useShadowRoot from '@/hooks/useShadowRoot';
import { useToolbarPosition } from '@/hooks/useToolbarPosition';
import { useWindowIsMobile } from '@/hooks/useWindowIsMobile';

/**
 * Add global onKeyDown event listeners for Keyboard navigating
 * through elements returned by the query selector
 * @param selector element query selector
 * @param active temporarily disable keyboard navigation for this instance
 * (e.g. when another dialog or dropdown is open and in focus taking priority for keyboard navigation)
 */
const useToolbarNavigation = (selector: string, active = true) => {
  const [a11yIndex, _setA11yIndex] = useState(-1);
  const a11yIndexRef = React.useRef(a11yIndex);
  const setA11yIndex = (v: number) => {
    a11yIndexRef.current = v;
    _setA11yIndex(v);
  };

  const position = useToolbarPosition();
  const isMobile = useWindowIsMobile();

  const onArrowDown = (evt: KeyboardEvent, isNav = false) => {
    evt.preventDefault();
    // Click the dropdown or toolbar button on down arrow key
    const { next, nextIndex, target } = getElements(evt);
    if (
      !isNav &&
      target.getAttribute('data-id') === 'toolbar-btn' &&
      isMobile &&
      (position.includes('Top') || position.includes('Center'))
    ) {
      target.click();
    } else {
      setA11yIndex(nextIndex);
      next?.focus();
    }
    // console.log(`onArrowDown: `, selector, elements(), target, current);
  };

  const onArrowUp = (evt: KeyboardEvent, isNav = false) => {
    evt.preventDefault();
    // Click the dropdown or toolbar button on up arrow key
    const { previous, previousIndex, target } = getElements(evt);
    if (
      !isNav &&
      target.getAttribute('data-id') === 'toolbar-btn' &&
      isMobile &&
      position.includes('Bottom')
    ) {
      target.click();
    } else {
      setA11yIndex(previousIndex);
      previous?.focus();
    }
    // console.log(`onArrowUp: `, selector, elements(), target, current);
  };

  const onArrowRight = (evt: KeyboardEvent) => {
    const { target } = getElements(evt);
    // Click the dropdown or toolbar button on right arrow key
    if (
      (target.getAttribute('data-id') === 'toolbar-btn' &&
        !isMobile &&
        position.includes('Left')) ||
      target.getAttribute('data-dropdown')
    ) {
      evt.preventDefault();
      target.click();
      // Set focus to the next element
    } else onArrowDown(evt, true);
  };

  const onArrowLeft = (evt: KeyboardEvent) => {
    const { target } = getElements(evt);
    // Click the dropdown or toolbar button on right arrow key
    if (
      target.getAttribute('data-id') === 'toolbar-btn' &&
      !isMobile &&
      position.includes('Right')
    ) {
      evt.preventDefault();
      target.click();
    } // Set focus to previous element
    else onArrowUp(evt, true);
  };

  const getElements = (evt: KeyboardEvent) => {
    const shadowElements = evt.composedPath();
    const shadowElement = shadowElements.length ? shadowElements[0] : null;

    // Get a new element list for each key event as the element ordering
    // may be different between keyboard interactions
    const list = elements();

    const max = (list?.length || 1) - 1; // Starting at 0
    const min = 0;

    // Find the current target in the element list
    const elIndex = list.findIndex(e => e === shadowElement);

    // Use the current index if we can't find an element index in the list
    const curIndex = elIndex !== -1 ? elIndex : current;

    // Get the elements from the list
    const target = list?.[curIndex === -1 ? 0 : curIndex] as HTMLElement;
    const nextIndex = curIndex >= max ? 0 : curIndex + 1;
    const next = list?.[nextIndex] as HTMLElement;
    const previousIndex = curIndex <= min ? max : curIndex - 1;
    const previous = list?.[previousIndex] as HTMLElement;

    return {
      list,
      current: curIndex,
      target,
      next,
      nextIndex,
      previous,
      previousIndex,
    };
  };

  const root = useShadowRoot();

  /** Array of all visible elements matching the querySelector in the shadowRoot */
  const elements = () =>
    Array.from(root?.querySelectorAll(selector) || []).filter(el =>
      el.checkVisibility({ visibilityProperty: true })
    );

  const current = a11yIndexRef.current; // Starting at -1

  const onKeydown = (evt: KeyboardEvent) => {
    if (active) {
      if (evt.key === 'ArrowDown') onArrowDown(evt);
      if (evt.key === 'ArrowUp') onArrowUp(evt);
      if (evt.key === 'ArrowRight') onArrowRight(evt);
      if (evt.key === 'ArrowLeft') onArrowLeft(evt);
    }
  };

  useEventListener('keydown', onKeydown);
};

export { useToolbarNavigation };
